import React from 'react';
import '../App.css';
import { NavBar } from './NavBar';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { RatingJobsPage } from '../pages/RatingJobsPage';
import { CompaniesPage } from '../pages/CompaniesPage';
import { LoginPage } from '../pages/LoginPage';
import { AddCompanyPage } from '../pages/AddCompanyPage';
import { AddJobPage } from '../pages/AddJobPage';
import { JobDetailPage } from '../pages/JobDetailPage';
import { AdminPage } from '../pages/AdminPage';
import { InvoiceDownloaderPage } from '../pages/InvoiceDownloaderPage';
import { ErrorBoundary } from './ErrorBoundary';

export interface MainComponentProps {
  skipTokenCheck?: boolean;
}

export interface Page {
  route: string;
  title: string;
  navBar: boolean; // whether to show in the navbar
  component: React.FC<MainComponentProps>;
}

const pageMap: Record<string, Page> = {
  LOGIN: {
    route: '/',
    title: 'Login Page',
    navBar: true,
    component: LoginPage,
  },
  COMPANIES: {
    route: '/companies',
    title: 'Companies',
    navBar: true,
    component: CompaniesPage,
  },
  ADD_COMPANY: {
    route: '/add-company',
    title: 'Add Company',
    navBar: false,
    component: AddCompanyPage,
  },
  RATING_JOBS: {
    route: '/rating-jobs',
    title: 'Rating Jobs',
    navBar: true,
    component: RatingJobsPage,
  },
  ADD_JOB: {
    route: '/add-job',
    title: 'Add Job',
    navBar: false,
    component: AddJobPage,
  },
  JOB_DETAIL: {
    route: '/job/:jobId',
    title: 'Job Detail',
    navBar: false,
    component: JobDetailPage,
  },
  ADMIN: {
    route: '/admin',
    title: 'Admin',
    navBar: false,
    component: AdminPage,
  },
  INVOICES: {
    route: '/invoices',
    title: 'Invoice Downloader',
    navBar: true,
    component: InvoiceDownloaderPage,
  },
};

export const pages: Page[] = Object.entries(pageMap).map(([type, page]) => ({
  type,
  ...page,
}));

/**
 * Wraps a page component with an Error Boundary for isolated error handling.
 * This allows one page to fail without crashing the entire application.
 */
const PageWithErrorBoundary: React.FC<{
  component: React.FC<MainComponentProps>;
  route: string;
}> = ({ component: Component, route }) => {
  return (
    <ErrorBoundary
      resetKeys={[route]}
      onError={(error, errorInfo) => {
        console.error(`Error in page ${route}:`, error, errorInfo);
        // Page-specific error handling can be added here
      }}
    >
      <Component />
    </ErrorBoundary>
  );
};

export const RouterComponent: React.FC = () => {
  return (
    <Router>
      <NavBar />
      <Routes>
        {pages.map((page: Page) => {
          return (
            <Route
              key={page.route}
              path={page.route}
              element={
                <PageWithErrorBoundary
                  component={page.component}
                  route={page.route}
                />
              }
            />
          );
        })}
      </Routes>
    </Router>
  );
};
